import { useState } from 'react'
import { CommandCenterShell } from '../../shell'
import { StatusBadge } from '../OperationsMiniPortal'
import { AGENT_COMMAND_ACCENT } from '../operationsModules'
import type { CommandDispatchState, CommandInterpretation } from '../../../../hooks/useCommandDispatch'
import type { ApprovalQueueRow } from './agentCommandQueue'
import '../operations-surface.css'

/**
 * OperationsAgentCommandCenter — the real Operations entry point to the
 * canonical command dispatcher and a live, read-only view of the agent
 * approval queue.
 *
 * Honesty: dispatch runs through the EXACT SAME /api/commands/dispatch
 * registry every other surface uses (via useCommandDispatch, unchanged —
 * no new dispatcher, no new CommandSurface). The approval queue reads
 * sq_agent_action_requests live via the existing Agent Workspace API.
 * Approving or rejecting a request is NOT re-implemented here — it stays
 * on the Agent Workspace surface (linked, labelled as a legacy fallback).
 *
 * CHAT #3 conversion: content roots adopt the shared `.sqv-surface` foundation
 * (canonical #0b0d17) and all presentation moved into operations-surface.css.
 * The only inline style is the DYNAMIC surface accent (--sqv-accent).
 * Presentational + SSR-safe; the route wrapper owns both live hooks.
 */
export interface OperationsAgentCommandCenterProps {
  dispatch: CommandDispatchState
  onExecute: (input: string) => void
  onRunProposal: () => void
  onClear: () => void
  onSendToAgent: (text: string) => void
  queue: ApprovalQueueRow[]
  queueLoading: boolean
  queueError: string | null
  queueAuthRequired: boolean
  onReloadQueue: () => void
  onHome?: () => void
  onBackCommandCenter?: () => void
  onBackOperations?: () => void
  onOpenRoute?: (to: string) => void
}

const accentVar = { ['--sqv-accent']: AGENT_COMMAND_ACCENT } as React.CSSProperties

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="sqv-ops-section">
      <h3 className="sqv-ops-section__title">{title}</h3>
      {children}
    </section>
  )
}

function InterpretationPanel({ interpretation, onRunProposal, onSendToAgent, onClear }: {
  interpretation: CommandInterpretation
  onRunProposal: () => void
  onSendToAgent: () => void
  onClear: () => void
}) {
  return (
    <div className="sqv-ops-interp" data-kind={interpretation.kind}>
      <div className="sqv-ops-interp__head">
        <span className="sqv-ops-interp__kind" data-kind={interpretation.kind}>
          {interpretation.kind === 'proposal' ? 'Interpreted command' : interpretation.kind === 'answer' ? 'Compass' : interpretation.kind === 'needs_setup' ? 'Setup required' : 'Not a command'}
        </span>
        <button type="button" onClick={onClear} aria-label="dismiss" className="sqv-ops-dismiss">✕</button>
      </div>
      {interpretation.kind === 'proposal' ? (
        <>
          <p className="sqv-ops-interp__text">
            {interpretation.intent.message ?? ''}{' '}
            <span className="sqv-ops-interp__cmd">→ {interpretation.intent.command_id}</span>
          </p>
          <div className="sqv-ops-interp__actions">
            <button type="button" onClick={onRunProposal} className="sqv-ops-btn">Run this</button>
            <button type="button" onClick={onSendToAgent} className="sqv-ops-ghost">Send to agent instead</button>
          </div>
        </>
      ) : interpretation.kind === 'answer' ? (
        <p className="sqv-ops-interp__answer">{interpretation.message}</p>
      ) : (
        <>
          <p className="sqv-ops-interp__text">{interpretation.message}</p>
          {interpretation.kind === 'unrecognized' && (
            <button type="button" onClick={onSendToAgent} className="sqv-ops-btn">Send to Agent Workspace →</button>
          )}
        </>
      )}
    </div>
  )
}

function DispatchPanel({ dispatch, onExecute, onRunProposal, onClear, onSendToAgent }: {
  dispatch: CommandDispatchState
  onExecute: (input: string) => void
  onRunProposal: () => void
  onClear: () => void
  onSendToAgent: () => void
}) {
  const [value, setValue] = useState('')
  const dispatching = dispatch.status === 'dispatching' || dispatch.status === 'interpreting'
  const run = () => {
    const v = value.trim()
    if (!v) return
    onExecute(v)
    setValue('')
  }
  return (
    <div className="sqv-ops-dispatch">
      {dispatch.interpretation && (
        <InterpretationPanel interpretation={dispatch.interpretation} onRunProposal={onRunProposal} onSendToAgent={onSendToAgent} onClear={onClear} />
      )}
      <div className="sqv-ops-dispatch__row">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') run() }}
          placeholder="Computer, execute…"
          aria-label="Type a command to dispatch"
          disabled={dispatching}
          className="sqv-ops-input sqv-ops-dispatch__input"
        />
        <button type="button" onClick={run} disabled={!value.trim() || dispatching} className="sqv-ops-btn">
          {dispatching ? 'Sending…' : 'Execute'}
        </button>
      </div>
      {dispatch.status !== 'idle' && dispatch.message && (
        <p className="sqv-ops-dispatch-msg" data-error={dispatch.status === 'error'}>{dispatch.message}</p>
      )}
    </div>
  )
}

function QueueRow({ row }: { row: ApprovalQueueRow }) {
  return (
    <div className="sqv-ops-qrow">
      <div className="sqv-ops-qrow__main">
        <span className="sqv-ops-qrow__title">{row.title}</span>
        <span className="sqv-ops-qrow__sub">
          {row.action_type} · {row.approval_status} · {row.execution_status}
          {row.prepared_by_agent_id ? ` · ${row.prepared_by_agent_id}` : ''}
        </span>
      </div>
      <span className="sqv-ops-risk" data-risk={row.risk_tier}>
        {row.risk_tier.toUpperCase()}
      </span>
    </div>
  )
}

export default function OperationsAgentCommandCenter(props: OperationsAgentCommandCenterProps) {
  const {
    dispatch, onExecute, onRunProposal, onClear, onSendToAgent,
    queue, queueLoading, queueError, queueAuthRequired, onReloadQueue,
    onHome, onBackCommandCenter, onBackOperations, onOpenRoute,
  } = props

  const rightRail = (
    <div className="sqv-surface sqv-ops sqv-ops-rail" style={accentVar}>
      <Section title="Status & readiness">
        <div className="sqv-ops-rail__status">
          <StatusBadge status="OPEN" />
          <p className="sqv-ops-backend">
            Dispatch runs through the existing command registry (<code className="sqv-ops-code">sq_commands</code>); the queue reads <code className="sqv-ops-code">sq_agent_action_requests</code> live. Typed input only here — voice capture stays on the shell command bar.
          </p>
          <button
            type="button"
            onClick={() => onOpenRoute?.('/square/command-center/agents')}
            title="Legacy fallback — approve or reject a request. Opens the current /square shell."
            className="sqv-ops-related"
          >
            Agent Workspace — approve/reject →
            <span className="sqv-ops-legacy-tag">LEGACY FALLBACK</span>
          </button>
        </div>
      </Section>
      <Section title="Blockers & setup">
        <p className="sqv-ops-blockers">
          Approving or rejecting a request still happens on the Agent Workspace surface — this workroom dispatches commands and shows the queue live, but does not decide requests.
        </p>
      </Section>
    </div>
  )

  let queueBody: React.ReactNode
  if (queueAuthRequired) {
    queueBody = <p className="sqv-ops-queue-msg">Sign in to the live cockpit to see the approval queue.</p>
  } else if (queueLoading && queue.length === 0) {
    queueBody = <p className="sqv-ops-queue-msg">Loading…</p>
  } else if (queueError) {
    queueBody = (
      <>
        <p className="sqv-ops-queue-err--warn">{queueError}</p>
        <button type="button" onClick={onReloadQueue} className="sqv-ops-ghost">Retry</button>
      </>
    )
  } else if (queue.length === 0) {
    queueBody = <p className="sqv-ops-queue-msg">No agent action requests yet.</p>
  } else {
    queueBody = (
      <div className="sqv-ops-list">
        {queue.map((row) => <QueueRow key={row.id} row={row} />)}
      </div>
    )
  }

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Operations', onClick: onBackOperations }}
      breadcrumb={['COMMAND CENTER', 'OPERATIONS', 'AGENT COMMAND']}
      title="Agent Command"
      titleAccent={AGENT_COMMAND_ACCENT}
      statusBadge={<StatusBadge status="OPEN" />}
      honestyBadge={{
        label: 'WORKING PORTAL · NOT FAKED',
        title: 'Dispatch runs through the real command registry (same route every surface uses). The approval queue reads real agent action requests live. Approve/reject stays on the Agent Workspace surface — not re-implemented here.',
      }}
      contextBar={<span className="sqv-ops-context__purpose">Dispatch a typed command through the canonical registry and review the live agent-approval queue.</span>}
      rightRail={rightRail}
    >
      <div className="sqv-surface sqv-ops sqv-ops-work" style={accentVar}>
        <Section title="Command dispatch">
          <DispatchPanel dispatch={dispatch} onExecute={onExecute} onRunProposal={onRunProposal} onClear={onClear} onSendToAgent={() => onSendToAgent(dispatch.lastInput ?? '')} />
        </Section>
        <Section title="Agent approval queue">
          {queueBody}
        </Section>
      </div>
    </CommandCenterShell>
  )
}
